import {
  marketStatusMapFrom, marketStatusPatchId, mergeMarketWithStatusPatch,
  oddStateIdKey, resolveOddFlash,
} from './sportsnewListPatch.js';
import {
  mergeOddPreservingIdentity,
  oddItemId, oddPatchId,
} from './sportsnewOddProjection.js';

const marketFamilyOf = (m) => m?.marketFamily ?? m?.marketCategoryKey ?? null;

/* ── Stable identity key for a MARKET (not a selection/odd within it) — a
   single marketStateId can legitimately be reused across multiple DISTINCT
   markets (different lines, periods, or team-scoped variants), so keying/
   deduping/grouping by marketStateId ALONE collapses them into one, which is
   exactly why "markets with multiple lines only show one". Prefers the API's
   own marketGroupId (the single most precise identity available); falls
   back to a composite of every dimension that can distinguish two markets
   sharing a marketStateId: family, period, line, team side, and
   selection-set shape. ── */
export const marketIdentityKey = (market) => {
  if (!market) return null;
  if (market.marketGroupId != null) return `mg:${market.marketGroupId}`;

  const marketStateId = market.marketStateId ?? market.id ?? market.market_id ?? null;
  const marketFamily = marketFamilyOf(market);
  const periodScope = market.periodScope ?? null;
  const lineKey = market.canonicalLineKey ?? market.lineKey ?? market.line ?? market.points ?? null;
  const teamSide = market.teamSide ?? market.team ?? null;
  const selectionSetKey = market.selectionSetKey ?? market.selectionSetId ?? null;

  const parts = [marketStateId, marketFamily, periodScope, lineKey, teamSide, selectionSetKey];
  // No identity dimension available at all — never collapse two such
  // markets against each other (matches the prior "unnamed" bucket
  // behavior for a market with no marketStateId).
  if (parts.every((p) => p == null)) return null;

  return ['fb', ...parts.map((p) => String(p ?? ''))].join('|');
};

/* ── Insert a genuinely NEW oddStateId (one not already present in any
   market's selections/odds) into the market/line group it belongs to.
   detail-panel fixture patches (`patch.odds[]` / a single top-level
   `patch.oddStateId`) previously only ever UPDATED an existing odd in place —
   a brand-new oddStateId (a line just being added, e.g. a fresh total/
   handicap line opening) was silently invisible until the next full page
   reload.
   Never places by marketStateId alone — multiple DISTINCT markets (lines/
   periods/team-scoped variants) can share one marketStateId. Priority:
   1) the odd's own marketGroupId, if present — most precise.
   2) marketStateId, narrowed by family/period/line/teamSide when more than
      one market shares that marketStateId.
   3) family/periodScope alone, when the odd carries no marketStateId at all.
   Any remaining ambiguity (more than one candidate, or none) returns
   inserted:false so the caller can recover instead of guessing wrong. ── */
export const insertOddIntoMarkets = (markets, odd) => {
  if (!Array.isArray(markets) || !odd || odd.oddStateId == null) {
    return { markets, inserted: false };
  }

  const alreadyPresent = markets.some((m) =>
    (m.selections ?? m.odds ?? []).some((o) => oddItemId(o) === oddItemId(odd))
  );
  if (alreadyPresent) return { markets, inserted: false };

  let targetIndex = -1;

  if (odd.marketGroupId != null) {
    targetIndex = markets.findIndex((m) => m.marketGroupId != null && String(m.marketGroupId) === String(odd.marketGroupId));
  }

  if (targetIndex === -1) {
    const targetMarketStateId = odd.marketStateId ?? odd.marketId ?? null;
    if (targetMarketStateId != null) {
      const candidates = [];
      markets.forEach((m, i) => {
        if (String(m.marketStateId ?? m.id ?? m.market_id) === String(targetMarketStateId)) candidates.push(i);
      });

      if (candidates.length === 1) {
        targetIndex = candidates[0];
      } else if (candidates.length > 1) {
        // Multiple markets share this marketStateId — narrow using whichever
        // of the odd's own identity fields are present. Never guess: if
        // narrowing still leaves more than one (or filters out all of them),
        // leave targetIndex unset.
        const oddFamily = odd.marketFamily ?? odd.marketCategoryKey ?? null;
        const oddPeriod = odd.periodScope ?? null;
        const oddLine = odd.canonicalLineKey ?? odd.lineKey ?? (odd.line != null ? String(odd.line) : null);
        const oddTeam = odd.teamSide ?? odd.team ?? null;

        const narrowed = candidates.filter((i) => {
          const m = markets[i];
          const familyOk = oddFamily == null || marketFamilyOf(m) === oddFamily;
          const periodOk = oddPeriod == null || (m.periodScope ?? null) === oddPeriod;
          const lineOk = oddLine == null || String(m.canonicalLineKey ?? m.lineKey ?? m.line ?? '') === String(oddLine);
          const teamOk = oddTeam == null || (m.teamSide ?? m.team ?? null) === oddTeam;
          return familyOk && periodOk && lineOk && teamOk;
        });
        if (narrowed.length === 1) targetIndex = narrowed[0];
      }
    } else {
      const oddFamily = odd.marketFamily ?? odd.marketCategoryKey ?? null;
      if (oddFamily != null) {
        const oddPeriod = odd.periodScope ?? null;
        const candidates = [];
        markets.forEach((m, i) => {
          if (marketFamilyOf(m) === oddFamily && (m.periodScope ?? null) === oddPeriod) candidates.push(i);
        });
        // Exactly one candidate market — safe to place it there. More than one
        // (e.g. two markets sharing the same family/period) is ambiguous: we
        // cannot know which one this line belongs to, so don't guess.
        if (candidates.length === 1) targetIndex = candidates[0];
      }
    }
  }

  if (targetIndex === -1) return { markets, inserted: false };

  const key = markets[targetIndex].selections ? 'selections' : 'odds';
  const nextMarkets = markets.slice();
  nextMarkets[targetIndex] = {
    ...markets[targetIndex],
    [key]: [...(markets[targetIndex][key] ?? []), odd],
  };
  return { markets: nextMarkets, inserted: true };
};

/* ── Deduplicate markets by stable identity (see marketIdentityKey — never
   marketStateId alone, since it can be reused across distinct lines/periods/
   team-scoped variants) and odds within each by oddStateId. ── */
export const dedupMarkets = (markets) => {
  if (!Array.isArray(markets)) return markets;
  const mktMap = new Map();
  const unkeyed = [];
  for (const mkt of markets) {
    const key = marketIdentityKey(mkt);
    if (key != null) {
      if (!mktMap.has(key)) mktMap.set(key, mkt);
    } else {
      unkeyed.push(mkt);
    }
  }
  return [...Array.from(mktMap.values()), ...unkeyed].map((mkt) => {
    const selKey = mkt.selections ? 'selections' : mkt.odds ? 'odds' : null;
    if (!selKey) return mkt;
    const oddMap = new Map();
    const unnamedOdds = [];
    for (const odd of mkt[selKey]) {
      const oid = oddItemId(odd);
      if (oid != null) {
        if (!oddMap.has(oid)) oddMap.set(oid, odd);
      } else {
        unnamedOdds.push(odd);
      }
    }
    return { ...mkt, [selKey]: [...Array.from(oddMap.values()), ...unnamedOdds] };
  });
};

const marketPatchItems = (market) => market?.selections ?? market?.odds ?? [];

const patchMarketArray = (patch) => (
  Array.isArray(patch?.marketSnapshot) ? patch.marketSnapshot : patch?.markets
);

const isExplicitFullMarketProjection = (patch) => (
  Array.isArray(patch?.marketSnapshot) ||
  patch?.isFullFixtureProjection === true ||
  patch?.is_full_fixture_projection === true ||
  String(patch?.projectionScope ?? patch?.projection_scope ?? '').toLowerCase() === 'full' ||
  patch?.replaceReason != null ||
  patch?.replace_reason != null
);

export const isExplicitEmptyFullMarketProjection = (patch) => (
  Array.isArray(patchMarketArray(patch))
  && patchMarketArray(patch).length === 0
  && isExplicitFullMarketProjection(patch)
);

const detailMarketMergeKey = (market) => {
  const key = marketIdentityKey(market);
  if (key != null) return key;
  const id = marketStatusPatchId(market);
  return id != null ? `state:${id}` : null;
};

const marketItemKey = (incomingMarket, existingMarket) => {
  if (incomingMarket?.selections) return 'selections';
  if (incomingMarket?.odds) return 'odds';
  if (existingMarket?.selections) return 'selections';
  if (existingMarket?.odds) return 'odds';
  return null;
};

const mergeMarketItemsPreservingExisting = (existingMarket, incomingMarket) => {
  const itemKey = marketItemKey(incomingMarket, existingMarket);
  if (!itemKey) return { ...existingMarket, ...incomingMarket };

  const existingItems = Array.isArray(existingMarket?.[itemKey]) ? existingMarket[itemKey] : [];
  const incomingItems = Array.isArray(incomingMarket?.[itemKey]) ? incomingMarket[itemKey] : [];
  const mergedById = new Map();
  const unnamedItems = [];

  existingItems.forEach((item) => {
    const id = item?.oddStateId ?? item?.id;
    if (id != null) mergedById.set(String(id), item);
    else unnamedItems.push(item);
  });
  incomingItems.forEach((item) => {
    const id = item?.oddStateId ?? item?.id;
    if (id != null) {
      const previous = mergedById.get(String(id));
      mergedById.set(String(id), previous ? mergeOddPreservingIdentity(previous, item) : item);
    } else {
      unnamedItems.push(item);
    }
  });

  return {
    ...existingMarket,
    ...incomingMarket,
    [itemKey]: [...Array.from(mergedById.values()), ...unnamedItems],
  };
};

const mergePartialMarketSnapshots = (markets, incomingMarkets, flashEvents, markRecovery) => {
  let nextMarkets = Array.isArray(markets) ? markets.slice() : [];
  const oldOddsMap = new Map();
  nextMarkets.forEach((mkt) => marketPatchItems(mkt).forEach((o) => {
    if (o.oddStateId != null) oldOddsMap.set(oddItemId(o), o);
  }));

  incomingMarkets.forEach((incomingMarket) => {
    marketPatchItems(incomingMarket).forEach((o) => {
      if (o.oddStateId == null) return;
      const direction = resolveOddFlash(oldOddsMap.get(oddItemId(o)), o);
      if (direction) flashEvents.push({ oddStateId: o.oddStateId, direction });
    });

    const incomingKey = detailMarketMergeKey(incomingMarket);
    if (incomingKey == null) {
      nextMarkets.push(incomingMarket);
      markRecovery();
      return;
    }

    const existingIndex = nextMarkets.findIndex((market) => detailMarketMergeKey(market) === incomingKey);
    if (existingIndex === -1) {
      nextMarkets.push(incomingMarket);
      return;
    }

    nextMarkets[existingIndex] = mergeMarketItemsPreservingExisting(nextMarkets[existingIndex], incomingMarket);
  });

  return dedupMarkets(nextMarkets);
};

/**
 * Merge a sportsnew:fixture:patch's markets[] AND odds[] into the current
 * detailMarkets array. These are INDEPENDENT concerns that can BOTH be
 * present, non-empty, in the SAME real payload — a prior if/else-if between
 * them in SportsnewContext.jsx silently dropped whichever one came second.
 * Both are always processed here, in order: markets[] first (explicit full
 * projection replaces the tree; otherwise renderable market patches upsert
 * without pruning siblings, and status-stubs merge bettable/suspended), then odds[] on top of that result (update
 * existing oddStateIds, or insert/upsert a new one when identity is
 * sufficient — see insertOddIntoMarkets). A lone top-level `oddStateId` is
 * an alternate SHAPE for the same "odds changed" concept as odds[] (never
 * both present in one real payload) but is still independent of markets[].
 *
 * Pure — returns the next markets array plus flash events and a recovery
 * signal as plain data; the caller (SportsnewContext) is responsible for the
 * actual flashOdds/setDetailMarkets/recoverFixture side effects.
 */
export const applyFixtureDetailPatch = (prevMarkets, patch) => {
  // A full empty snapshot supersedes independent stale odds in the same event.
  // It must not insert rows or ask recovery to recreate the removed markets.
  if (isExplicitEmptyFullMarketProjection(patch)) {
    return { markets: [], flashEvents: [], needsRecovery: false };
  }
  let workingMarkets = Array.isArray(prevMarkets) ? prevMarkets : [];
  const flashEvents = [];
  let needsRecovery = false;

  const incomingMarkets = patchMarketArray(patch);
  if (Array.isArray(incomingMarkets)) {
    const patchHasOdds = incomingMarkets.some(
      (m) => (m.selections ?? m.odds ?? []).length > 0
    );
    if (patchHasOdds && isExplicitFullMarketProjection(patch)) {
      // Diff against what's currently shown BEFORE it's overwritten, so
      // every changed price OR lock/unlock transition flashes, not just the
      // ones a separate top-level oddStateId/odds[] field happens to call out.
      const oldOddsMap = new Map();
      workingMarkets.forEach((mkt) => (mkt.selections ?? mkt.odds ?? []).forEach((o) => {
        if (o.oddStateId != null) oldOddsMap.set(oddItemId(o), o);
      }));
      incomingMarkets.forEach((mkt) => (mkt.selections ?? mkt.odds ?? []).forEach((o) => {
        if (o.oddStateId == null) return;
        const direction = resolveOddFlash(oldOddsMap.get(oddItemId(o)), o);
        if (direction) flashEvents.push({ oddStateId: o.oddStateId, direction });
      }));
      workingMarkets = dedupMarkets(incomingMarkets);
    } else if (patchHasOdds) {
      workingMarkets = mergePartialMarketSnapshots(
        workingMarkets,
        incomingMarkets,
        flashEvents,
        () => { needsRecovery = true; }
      );
    } else {
      // Status-only stubs — merge bettable/suspended into existing markets.
      // If a live push announces a market that the REST-loaded detail panel has
      // never seen, this stub has no selections to render; trigger the existing
      // fixture recovery path so the full market/selection snapshot is fetched.
      const knownMarketStateIds = new Set(
        workingMarkets
          .map((mkt) => mkt?.marketStateId ?? mkt?.id ?? mkt?.market_id)
          .filter((id) => id != null)
          .map(String)
      );
      const hasUnknownMarket = incomingMarkets.some((mkt) => {
        const id = mkt?.marketStateId ?? mkt?.id ?? mkt?.market_id;
        return id != null && !knownMarketStateIds.has(String(id));
      });
      if (hasUnknownMarket) needsRecovery = true;

      const statusMap = marketStatusMapFrom(incomingMarkets);
      workingMarkets = workingMarkets.map((mkt) => {
        const upd = statusMap.get(marketStatusPatchId(mkt));
        if (!upd) return mkt;
        return mergeMarketWithStatusPatch(mkt, upd, flashEvents).market;
      });
    }
  }

  if (Array.isArray(patch?.odds)) {
    // Bulk changed-odds array — processed independently of patch.markets[]
    // above, on top of workingMarkets (the just-applied markets[] result if
    // that ran above, otherwise the previous detailMarkets untouched).
    const prevOddsMap = new Map();
    workingMarkets.forEach((mkt) => (mkt.selections ?? mkt.odds ?? []).forEach((o) => {
      if (o.oddStateId != null) prevOddsMap.set(oddItemId(o), o);
    }));

    const updById = new Map();
    patch.odds.forEach((od) => {
      const oid = od.oddStateId ?? od.id;
      if (oid != null) updById.set(oddStateIdKey(oid), od);
    });

    let nextMarkets = workingMarkets.map((mkt) => {
      const key = mkt.selections ? 'selections' : mkt.odds ? 'odds' : null;
      if (!key) return mkt;
      let changed = false;
      const items = mkt[key].map((o) => {
        const itemId = oddItemId(o);
        const upd = updById.get(itemId);
        if (!upd || !prevOddsMap.has(itemId)) return o;
        changed = true;
        const merged = mergeOddPreservingIdentity(o, upd);
        const direction = resolveOddFlash(o, merged);
        if (direction) flashEvents.push({ oddStateId: o.oddStateId, direction });
        return merged;
      });
      return changed ? { ...mkt, [key]: items } : mkt;
    });

    // Any oddStateId in the patch that ISN'T an update to an existing
    // selection is a genuinely new one — insert it into its market/line
    // group, or flag a recovery if we can't place it. Never silently ignore
    // it (that previously left a newly-opened line invisible).
    updById.forEach((od, oid) => {
      if (prevOddsMap.has(oid)) return;
      const { markets: withInsert, inserted } = insertOddIntoMarkets(nextMarkets, od);
      if (inserted) nextMarkets = withInsert;
      else needsRecovery = true;
    });

    workingMarkets = nextMarkets;
  } else if (patch?.oddStateId != null) {
    // Individual odd update — patch in-place and flash price OR lock/unlock
    // transitions; if this oddStateId isn't known yet, it's an insert, not
    // an update (see insertOddIntoMarkets).
    let oldOdd = null;
    for (const mkt of workingMarkets) {
      const items = mkt.selections ?? mkt.odds ?? [];
      const found = items.find((o) => oddItemId(o) === oddPatchId(patch));
      if (found) { oldOdd = found; break; }
    }

    if (oldOdd) {
      const merged = mergeOddPreservingIdentity(oldOdd, patch);
      const direction = resolveOddFlash(oldOdd, merged);
      if (direction) flashEvents.push({ oddStateId: patch.oddStateId, direction });
      workingMarkets = workingMarkets.map((mkt) => {
        const key = mkt.selections ? 'selections' : mkt.odds ? 'odds' : null;
        if (!key) return mkt;
        if (!mkt[key].some((o) => oddItemId(o) === oddPatchId(patch))) return mkt;
        return { ...mkt, [key]: mkt[key].map((o) => oddItemId(o) === oddPatchId(patch) ? mergeOddPreservingIdentity(o, patch) : o) };
      });
    } else {
      const { markets: withInsert, inserted } = insertOddIntoMarkets(workingMarkets, patch);
      if (inserted) workingMarkets = withInsert;
      else needsRecovery = true;
    }
  }

  return { markets: workingMarkets, flashEvents, needsRecovery };
};
